import React from 'react';

/**
 * 一覧の試聴（`useAudioAudition`）用の再生 / 停止ボタン。
 *
 * ファイル一覧やテーブルの行内に置く小さなトグルで、`play_circle` /
 * `pause_circle` を切り替える。再生の状態管理は持たず、呼び出し側の
 * `useAudioAudition`（`playingKey` / `toggle`）に委ねる。
 *
 * variant:
 *  - `plain`   … サムネイルの代わりに置く（`MediaItemTable` のオーディオ行）
 *  - `overlay` … サムネイル画像の上に重ねる（`MySiteWorks` の作品登録表）。
 *                画像の明暗に負けないよう白 + ドロップシャドウで描く
 */
export type AuditionPlayButtonVariant = 'plain' | 'overlay';

export interface AuditionPlayButtonProps {
  playing: boolean;
  /** ボタンの外形サイズ(px)。アイコンはこの 0.8 倍で描く */
  size: number;
  onToggle: () => void;
  /** 既定 "plain" */
  variant?: AuditionPlayButtonVariant;
}

const AuditionPlayButton: React.FC<AuditionPlayButtonProps> = ({
  playing,
  size,
  onToggle,
  variant = 'plain',
}) => {
  const isOverlay = variant === 'overlay';
  // overlay は下地の画像が読めない色なので、再生状態はアイコンの形だけで示す。
  const color = isOverlay
    ? '#fff'
    : playing
      ? 'var(--soremo-blue)'
      : 'var(--soremo-light-gray)';

  return (
    <div
      className="u-col-center"
      style={{ width: size, height: size, flexShrink: 0 }}
    >
      <span
        className="material-symbols-rounded"
        role="button"
        aria-label={playing ? '停止' : '再生'}
        style={{
          fontSize: Math.round(size * 0.8),
          color,
          textShadow: isOverlay ? '0 1px 3px rgba(0, 0, 0, 0.5)' : undefined,
          cursor: 'pointer',
          lineHeight: 1,
          userSelect: 'none',
        }}
        // 行クリック（試聴行の選択など）を巻き込まない。
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        {playing ? 'pause_circle' : 'play_circle'}
      </span>
    </div>
  );
};

export default AuditionPlayButton;
